import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  Tooltip,
  Fab,
  IconButton,
  Paper,
  Typography,
  Box,
  makeStyles
} from '@material-ui/core';
import {
  Phone as PhoneIcon,
  Close as CloseIcon,
  Remove as MinimizeIcon,
  AspectRatio as MaximizeIcon,
  Refresh as RefreshIcon
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  fabContainer: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    zIndex: 99999,
    transition: 'all 0.25s ease-in-out',
  },
  fabHidden: {
    opacity: 0,
    pointerEvents: 'none',
    transform: 'scale(0.5)',
  },
  fabButton: {
    backgroundColor: '#00C853',
    color: '#ffffff',
    boxShadow: '0 4px 15px rgba(0, 200, 83, 0.4)',
    '&:hover': {
      backgroundColor: '#00B248',
      transform: 'scale(1.06)',
      boxShadow: '0 6px 20px rgba(0, 200, 83, 0.6)',
    },
    transition: 'all 0.25s ease-in-out',
  },
  fabPulsing: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    backgroundColor: '#00E676',
    opacity: 0.7,
    animation: '$pulse 1.8s infinite',
    zIndex: -1,
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
      opacity: 0.7,
    },
    '70%': {
      transform: 'scale(1.35)',
      opacity: 0,
    },
    '100%': {
      transform: 'scale(1.4)',
      opacity: 0,
    },
  },
  phoneModal: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    width: 370,
    height: 560,
    maxWidth: '92vw',
    maxHeight: '88vh',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.35)',
    zIndex: 99999,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#1E1E24',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease, transform 0.25s ease',
  },
  phoneModalHidden: {
    opacity: 0,
    pointerEvents: 'none',
    visibility: 'hidden',
    transform: 'scale(0.85) translateY(30px)',
  },
  phoneModalMinimized: {
    height: 48,
    overflow: 'hidden',
  },
  phoneHeader: {
    backgroundColor: '#121217',
    color: '#ffffff',
    padding: '8px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    userSelect: 'none',
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: '0.88rem',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    letterSpacing: '0.3px',
  },
  headerIconBtn: {
    color: 'rgba(255, 255, 255, 0.75)',
    padding: 5,
    marginLeft: 2,
    '&:hover': {
      color: '#ffffff',
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
  },
  iframeWrapper: {
    flex: 1,
    width: '100%',
    height: '100%',
    backgroundColor: '#ededed',
    overflow: 'hidden',
    transition: 'opacity 0.2s ease',
  },
  iframeWrapperMinimized: {
    height: 0,
    minHeight: 0,
    maxHeight: 0,
    flex: 0,
    overflow: 'hidden',
    opacity: 0,
    pointerEvents: 'none',
    visibility: 'hidden',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
}));

const GlobalWebphone = () => {
  const classes = useStyles();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [callUrl, setCallUrl] = useState('');
  const [activeRamal, setActiveRamal] = useState('');
  const iframeRef = useRef(null);

  // Validação de rota e autenticação
  const pathname = location?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  const isHiddenRoute = pathname === '/' || pathname === '/login' || pathname.startsWith('/login');

  const token = Cookies.get('token');
  const usuario = Cookies.get('usuario');
  const isUserValid = Boolean(
    token && token !== 'undefined' && token !== 'null' &&
    usuario && usuario !== 'undefined' && usuario !== 'null'
  );

  const shouldRender = !isHiddenRoute && isUserValid;

  // Obtém o usuário logado do cookie 'usuario'
  const getLoggedUsername = () => {
    return Cookies.get('usuario') || 'aislan';
  };

  // Monta a URL parametrizada do softphone
  const buildSoftphoneUrl = (ramal = '', customUser = '') => {
    const server = '162.240.226.254:6050';
    const user = customUser || getLoggedUsername();
    const pass = '999@@456';

    let url = `https://gruporosinetos.com/call/softphone.html?wp_serveraddress=${server}&wp_username=${encodeURIComponent(user)}&wp_password=${encodeURIComponent(pass)}`;

    if (ramal) {
      url += `&wp_callto=${encodeURIComponent(ramal)}&wp_autoaction=1`;
    }

    return url;
  };

  // Pré-carrega o softphone em segundo plano assim que o usuário estiver logado
  useEffect(() => {
    if (shouldRender && !callUrl) {
      setCallUrl(buildSoftphoneUrl(''));
    }
  }, [shouldRender, callUrl]);

  const openWebphone = (ramal = '', customUser = '') => {
    setActiveRamal(ramal);
    setIsOpen(true);
    setIsMinimized(false);

    if (ramal) {
      // Se o iframe já existe e está carregado, dispara a chamada diretamente sem recarregar a página!
      if (iframeRef.current && iframeRef.current.contentWindow && callUrl) {
        iframeRef.current.contentWindow.postMessage({ type: 'SIP_CALL', ramal: ramal }, '*');
      } else {
        const newUrl = buildSoftphoneUrl(ramal, customUser);
        setCallUrl(newUrl);
        if (iframeRef.current) {
          iframeRef.current.src = newUrl;
        }
      }
    }
  };

  const toggleOpen = () => {
    if (!isOpen) {
      setIsOpen(true);
      setIsMinimized(false);
    } else {
      setIsOpen(false);
    }
  };

  const handleRefresh = () => {
    if (iframeRef.current && callUrl) {
      iframeRef.current.src = callUrl;
    }
  };

  // Listener para evento customizado global de chamada (ex: das Cornetas)
  useEffect(() => {
    const handleGlobalCall = (event) => {
      const ramal = event.detail?.ramal || '';
      const customUser = event.detail?.usuario || '';
      if (ramal) {
        openWebphone(ramal, customUser);
      }
    };

    window.addEventListener('open-sip-call', handleGlobalCall);
    return () => {
      window.removeEventListener('open-sip-call', handleGlobalCall);
    };
  }, []);

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      {/* Botão Flutuante de Telefone (visível quando janela estiver fechada) */}
      <div className={`${classes.fabContainer} ${isOpen ? classes.fabHidden : ''}`}>
        <Tooltip title="Abrir Telefone / Softphone" placement="left" arrow>
          <Fab
            className={classes.fabButton}
            onClick={toggleOpen}
            aria-label="Telefone"
            size="medium"
          >
            <div className={classes.fabPulsing} />
            <PhoneIcon />
          </Fab>
        </Tooltip>
      </div>

      {/* Janela Flutuante do Softphone (permanece montada na DOM para não recarregar) */}
      <Paper
        className={`${classes.phoneModal} ${
          !isOpen ? classes.phoneModalHidden : isMinimized ? classes.phoneModalMinimized : ''
        }`}
        elevation={8}
      >
        {/* Cabeçalho da Janela */}
        <div
          className={classes.phoneHeader}
          onDoubleClick={() => setIsMinimized((prev) => !prev)}
        >
          <Typography
            className={classes.headerTitle}
            onClick={() => isMinimized && setIsMinimized(false)}
            style={{ cursor: isMinimized ? 'pointer' : 'default' }}
          >
            <PhoneIcon style={{ fontSize: 17, color: '#00E676' }} />
            {activeRamal ? `Chamando: ${activeRamal}` : 'Softphone Web'}
          </Typography>
          <Box display="flex" alignItems="center">
            <Tooltip title="Recarregar Softphone">
              <IconButton
                size="small"
                className={classes.headerIconBtn}
                onClick={handleRefresh}
              >
                <RefreshIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title={isMinimized ? 'Expandir' : 'Minimizar'}>
              <IconButton
                size="small"
                className={classes.headerIconBtn}
                onClick={() => setIsMinimized((prev) => !prev)}
              >
                {isMinimized ? (
                  <MaximizeIcon style={{ fontSize: 16 }} />
                ) : (
                  <MinimizeIcon style={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Fechar">
              <IconButton
                size="small"
                className={classes.headerIconBtn}
                onClick={() => {
                  setIsOpen(false);
                  setIsMinimized(false);
                }}
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </div>

        {/* Iframe do Softphone (NUNCA desmontado, mantém conexão SIP viva) */}
        <div className={`${classes.iframeWrapper} ${isMinimized ? classes.iframeWrapperMinimized : ''}`}>
          {callUrl && (
            <iframe
              ref={iframeRef}
              title="Softphone Web"
              src={callUrl}
              allow="microphone *; camera *; autoplay *"
              allowFullScreen={true}
              className={classes.iframe}
            />
          )}
        </div>
      </Paper>
    </>
  );
};

export default GlobalWebphone;
